import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  Collapse,
} from "reactstrap"
import { setBreadcrumbItems } from "store/actions"
import { connect } from "react-redux"
import apiClient, { API_BASE } from "helpers/apiClient"
import { formatIndianDateTime } from "helpers/indianDate"
import type { PropertyKycBlockReason } from "helpers/auth_api"
import KycRequiredModal from "components/KycRequiredModal/KycRequiredModal"
import BidRequestModal from "../Home/Properties/BidRequestModal"

const MY_BIDS_URL = "/api/bids/my-bids"

const DEFAULT_MY_BIDS_ERROR = "Failed to load your bids."

const extractAxiosErrorMessage = (err: unknown): string | null => {
  const data = (err as { response?: { data?: unknown } })?.response?.data
  if (data && typeof data === "object" && data !== null) {
    const o = data as Record<string, unknown>
    const m = o.message ?? o.error
    if (typeof m === "string" && m.trim()) return m.trim()
  }
  return null
}

/** When server returns a KYC gate message, use warning UI + KYC help modal variant. */
const kycVariantFromMessage = (msg: string): PropertyKycBlockReason | null => {
  if (/pending admin approval|administrator approves|kyc is pending/i.test(msg)) return "pending"
  if (/complete.*kyc|\bkyc\b|know your customer|verification required|upload.*documents/i.test(msg))
    return "upload"
  return null
}

interface ListingInfo {
  _id: string
  propertyTitle?: string
  address?: string
  city?: string
  state?: string
  biddingEnabled?: boolean
  biddingEndDate?: string
  auctionEndDate?: string
  auctionStartDate?: string
  biddingStartPrice?: number
  biddingMinIncrement?: number
  reservePrice?: number
  superAreaSqFt?: number
  pricePerSqFt?: number
  roiPercentage?: number
  annualRent?: number
  listingStatus?: string
  propertyImagesUrls?: string[]
}

interface BidItem {
  _id: string
  amount: number
  isLeading?: boolean
  createdAt: string
  listingId: ListingInfo | null
  pricePerSqFt?: number
  roiPercentage?: number
  description?: string
  clientName?: string
}

interface GroupedBids {
  listing: ListingInfo
  bids: BidItem[]
  isActive: boolean
  currentHighestBid: number
  userHighestBid: number
  isUserLeading: boolean
}

interface BreadcrumbItem {
  title: string
  link: string
}

interface MyBidsProps {
  setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) => void
}

const formatCurrency = (n: number) => "₹" + (n ?? 0).toLocaleString("en-IN")

const formatDateTime = (d: string) => formatIndianDateTime(d)

const MyBids = (props: MyBidsProps) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [groupedBids, setGroupedBids] = useState<GroupedBids[]>([])
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [bidModalOpen, setBidModalOpen] = useState(false)
  const [selectedListing, setSelectedListing] = useState<ListingInfo | null>(null)
  const [selectedCurrentBid, setSelectedCurrentBid] = useState<number | null>(null)
  const [kycHelpOpen, setKycHelpOpen] = useState(false)
  const [kycHelpVariant, setKycHelpVariant] = useState<PropertyKycBlockReason>("pending")

  useEffect(() => {
    props.setBreadcrumbItems("My Bids", [
      { title: "Bidding", link: "#" },
      { title: "My Bids", link: "#" },
    ])
  }, [props])

  const fetchMyBids = () => {
    setLoading(true)
    setError(null)
    setKycHelpOpen(false)
    apiClient
      .get<{ success: boolean; message?: string; data?: BidItem[] }>(MY_BIDS_URL)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          const bids = res.data.data
          const now = new Date()

          // Group bids by listing
          const listingMap = new Map<string, GroupedBids>()

          for (const bid of bids) {
            if (!bid.listingId || !bid.listingId._id) continue
            const listingId = bid.listingId._id

            if (!listingMap.has(listingId)) {
              const listing = bid.listingId
              const endDate = listing.auctionEndDate || listing.biddingEndDate
              const isActive = Boolean(listing.biddingEnabled && (!endDate || new Date(endDate) > now))

              listingMap.set(listingId, {
                listing,
                bids: [],
                isActive,
                currentHighestBid: 0,
                userHighestBid: 0,
                isUserLeading: false,
              })
            }

            const group = listingMap.get(listingId)!
            group.bids.push(bid)

            // Track user's highest bid
            if (bid.amount > group.userHighestBid) {
              group.userHighestBid = bid.amount
            }

            // Track if user is currently leading
            if (bid.isLeading) {
              group.isUserLeading = true
              group.currentHighestBid = bid.amount
            }
          }

          // Sort bids within each group by date (newest first)
          for (const group of listingMap.values()) {
            group.bids.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          }

          // Convert to array and sort by most recent bid
          const grouped = Array.from(listingMap.values()).sort((a, b) => {
            const aDate = a.bids[0]?.createdAt || ""
            const bDate = b.bids[0]?.createdAt || ""
            return new Date(bDate).getTime() - new Date(aDate).getTime()
          })

          setGroupedBids(grouped)
        } else if (res.data && res.data.success === false) {
          const raw =
            typeof res.data.message === "string" && res.data.message.trim()
              ? res.data.message.trim()
              : DEFAULT_MY_BIDS_ERROR
          setGroupedBids([])
          setError(raw)
          const kv = kycVariantFromMessage(raw)
          if (kv) setKycHelpVariant(kv)
        } else {
          setGroupedBids([])
        }
      })
      .catch((err: unknown) => {
        setGroupedBids([])
        const fromBody = extractAxiosErrorMessage(err)
        const msg = fromBody || DEFAULT_MY_BIDS_ERROR
        setError(msg)
        const kv = kycVariantFromMessage(msg)
        if (kv) setKycHelpVariant(kv)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchMyBids()
  }, [])

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  const openBidModal = (group: GroupedBids) => {
    setSelectedListing(group.listing)
    setSelectedCurrentBid(group.currentHighestBid || group.userHighestBid)
    setBidModalOpen(true)
  }

  const closeBidModal = () => {
    setBidModalOpen(false)
    setSelectedListing(null)
    setSelectedCurrentBid(null)
    fetchMyBids() // Refresh data after closing modal
  }

  if (loading) {
    return (
      <Row>
        <Col className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2 text-muted">Loading your bids...</p>
        </Col>
      </Row>
    )
  }

  if (error) {
    const kycVariant = kycVariantFromMessage(error)
    return (
      <div className="my-bids-page">
        <Row>
          <Col>
            <Alert color={kycVariant ? "warning" : "danger"} className="mb-3">
              <span className="fw-semibold d-block mb-1">{kycVariant ? "Account verification" : "Could not load bids"}</span>
              <span className="small mb-0 d-block">{error}</span>
            </Alert>
            <div className="d-flex flex-wrap gap-2">
              {kycVariant ? (
                <>
                  <Button tag={Link} to="/profile" color="primary">
                    Go to profile
                  </Button>
                  <Button color="primary" outline onClick={() => setKycHelpOpen(true)}>
                    About KYC
                  </Button>
                </>
              ) : (
                <Button color="primary" onClick={fetchMyBids}>
                  Retry
                </Button>
              )}
            </div>
          </Col>
        </Row>
        <KycRequiredModal isOpen={kycHelpOpen} toggle={() => setKycHelpOpen(false)} variant={kycHelpVariant} />
      </div>
    )
  }

  return (
    <div className="my-bids-page">
      <Row>
        <Col xl={12}>
          <Card className="mb-4">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle tag="h5" className="mb-0">
                <i className="mdi mdi-gavel me-2" />
                My Bids
              </CardTitle>
              <Button color="primary" size="sm" onClick={fetchMyBids}>
                <i className="mdi mdi-refresh me-1" />
                Refresh
              </Button>
            </CardHeader>
            <CardBody>
              <p className="text-muted mb-3">
                All properties you have bid on. Click on a row to see your bid history for that property.
              </p>

              {groupedBids.length === 0 ? (
                <Alert color="info">
                  <i className="mdi mdi-information-outline me-2" />
                  You haven't placed any bids yet.{" "}
                  <Link to="/properties">Browse properties</Link> to start bidding.
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table bordered hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 40 }}></th>
                        <th>Property</th>
                        <th className="text-center">Your Bids</th>
                        <th className="text-end">Your Highest</th>
                        <th className="text-center">Status</th>
                        <th style={{ width: 120 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedBids.map((group) => {
                        const id = group.listing._id
                        const isOpen = expandedId === id
                        return (
                          <React.Fragment key={id}>
                            <tr>
                              <td>
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0 text-dark"
                                  onClick={() => toggleExpand(id)}
                                  aria-expanded={isOpen}
                                >
                                  <i className={`mdi mdi-chevron-${isOpen ? "down" : "right"} fs-5`} />
                                </Button>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  {group.listing.propertyImagesUrls?.length ? (
                                    <img
                                      src={`${API_BASE}${group.listing.propertyImagesUrls[0]}`}
                                      alt=""
                                      style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }}
                                    />
                                  ) : (
                                    <div className="bg-light rounded" style={{ width: 48, height: 48 }} />
                                  )}
                                  <div>
                                    <strong>{group.listing.propertyTitle || "Untitled"}</strong>
                                    {(group.listing.city || group.listing.state) && (
                                      <div className="small text-muted">
                                        {[group.listing.city, group.listing.state].filter(Boolean).join(", ")}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="text-center">
                                <Badge color="primary" className="px-2">
                                  {group.bids.length}
                                </Badge>
                              </td>
                              <td className="text-end">
                                <strong className={group.isUserLeading ? "text-success" : ""}>
                                  {formatCurrency(group.userHighestBid)}
                                </strong>
                                {group.isUserLeading && (
                                  <div className="small text-success">
                                    <i className="mdi mdi-check-circle me-1" />
                                    Leading
                                  </div>
                                )}
                                {!group.isUserLeading && group.isActive && (
                                  <div className="small text-warning">
                                    <i className="mdi mdi-alert-circle me-1" />
                                    Outbid
                                  </div>
                                )}
                              </td>
                              <td className="text-center">
                                {group.isActive ? (
                                  <Badge color="success">
                                    <i className="mdi mdi-circle-small" />
                                    Active
                                  </Badge>
                                ) : (
                                  <Badge color="secondary">
                                    <i className="mdi mdi-circle-small" />
                                    Ended
                                  </Badge>
                                )}
                              </td>
                              <td>
                                {group.isActive ? (
                                  <Button
                                    color="success"
                                    size="sm"
                                    onClick={() => openBidModal(group)}
                                  >
                                    <i className="mdi mdi-gavel me-1" />
                                    Bid
                                  </Button>
                                ) : (
                                  <Link
                                    to={`/property/${id}`}
                                    className="btn btn-sm btn-outline-secondary"
                                  >
                                    <i className="mdi mdi-eye me-1" />
                                    View
                                  </Link>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={6} className="p-0 border-0">
                                <Collapse isOpen={isOpen}>
                                  <div className="p-3 bg-light">
                                    <h6 className="mb-2">
                                      <i className="mdi mdi-history me-1" />
                                      Your Bid History
                                    </h6>
                                    <Table size="sm" bordered className="mb-0 bg-white">
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Amount</th>
                                          <th>Price/Sq.ft</th>
                                          <th>Date & Time</th>
                                          <th>Status</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {group.bids.map((bid, idx) => (
                                          <tr key={bid._id}>
                                            <td>{idx + 1}</td>
                                            <td className="fw-semibold">{formatCurrency(bid.amount)}</td>
                                            <td>
                                              {bid.pricePerSqFt != null
                                                ? formatCurrency(bid.pricePerSqFt)
                                                : "—"}
                                            </td>
                                            <td className="text-muted">{formatDateTime(bid.createdAt)}</td>
                                            <td>
                                              {bid.isLeading ? (
                                                <Badge color="success">Leading</Badge>
                                              ) : (
                                                <Badge color="warning">Outbid</Badge>
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                    <div className="mt-2">
                                      <Link
                                        to={`/property/${id}`}
                                        className="btn btn-sm btn-outline-primary"
                                      >
                                        <i className="mdi mdi-eye me-1" />
                                        View Property Details
                                      </Link>
                                    </div>
                                  </div>
                                </Collapse>
                              </td>
                            </tr>
                          </React.Fragment>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              )}

              <div className="mt-3 text-muted small">
                Total: {groupedBids.length} properties bid on
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Bid Modal */}
      <BidRequestModal
        isOpen={bidModalOpen}
        toggle={closeBidModal}
        listingId={selectedListing?._id}
        startingBid={String(selectedListing?.biddingStartPrice ?? 0)}
        currentHighestBid={selectedCurrentBid}
        minBidIncrease={selectedListing?.biddingMinIncrement}
        listingStatus={selectedListing?.listingStatus}
        reservePrice={selectedListing?.reservePrice}
        pricePerSqFt={selectedListing?.pricePerSqFt}
        roiPercentage={selectedListing?.roiPercentage}
        superAreaSqFt={selectedListing?.superAreaSqFt}
        annualRent={selectedListing?.annualRent}
        propertyTitle={selectedListing?.propertyTitle}
        propertyAddress={[selectedListing?.address, selectedListing?.city, selectedListing?.state]
          .filter(Boolean)
          .join(", ")}
        auctionDate={selectedListing?.auctionStartDate}
        userHasBid={true}
        onSuccess={closeBidModal}
      />
    </div>
  )
}

const mapDispatchToProps = { setBreadcrumbItems }
export default connect(null, mapDispatchToProps)(MyBids)
